/**
 * UPOZORNENIA DO TELEFÓNU (.464) – zapnutie a voľby.
 *
 * Oto: „Nový mail, nová objednávka, faktúra po splatnosti, správa v tíme –
 * nech mi to zabzučí v telefóne, aj keď appku nemám otvorenú."
 *
 * ── Prečo to vyzerá takto ────────────────────────────────────────────────
 * Zapína sa to **na každom zariadení zvlášť**. Nie je to rozmar prehliadača:
 * povolenie patrí ku konkrétnemu telefónu a počítaču, takže „zapnuté" na
 * jednom mieste o druhom nehovorí nič. Preto je tu zoznam prihlásených
 * zariadení – bez neho by človek nevedel, či mu to bzučí na starom telefóne,
 * ktorý už nepoužíva.
 *
 * Povolenie sa **nepýta samo od seba**. Prehliadač vyskočí s otázkou až po
 * ťuknutí na tlačidlo; okno, ktoré sa pýta hneď po otvorení, väčšina ľudí
 * odmietne a späť sa to potom zapína ťažko (v nastaveniach stránky).
 *
 * A je tu skúšobné upozornenie. Je to jediný spôsob, ako zistiť, že celá
 * cesta – server, Google aj telefón – naozaj funguje; „zapnuté" bez toho
 * znamená len to, že sa nič nepokazilo pri zapínaní.
 */
import { useCallback, useEffect, useState } from 'react';
import { Bell, BellOff, Smartphone, Trash2, Send } from 'lucide-react';
import { api } from '../api';
import { useToast } from './ui';

/* Kľúč servera príde ako text (base64url); prehliadač ho chce ako bajty. */
function klucNaBajty(kluc) {
  const doplnene = `${kluc}${'='.repeat((4 - (kluc.length % 4)) % 4)}`
    .replace(/-/g, '+').replace(/_/g, '/');
  const surove = window.atob(doplnene);
  return Uint8Array.from([...surove].map((z) => z.charCodeAt(0)));
}

export const podporovane = () => (
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window);

export default function Upozornenia() {
  const toast = useToast();
  const [stav, setStav] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tuZapnute, setTuZapnute] = useState(null);

  const nacitaj = useCallback(async () => {
    try {
      const d = await api.get('/api/upozornenia/stav');
      setStav(d);
    } catch (e) { toast.error(e.message); }
  }, [toast]);

  useEffect(() => { nacitaj(); }, [nacitaj]);

  /* Či je zapnuté práve toto zariadenie, sa nedá vyčítať zo servera – server
   * vidí zoznam odberov, nie to, ktorý z nich je „ten tu". Pýtame sa preto
   * prehliadača. */
  useEffect(() => {
    if (!podporovane()) { setTuZapnute(false); return; }
    navigator.serviceWorker.ready
      .then((r) => r.pushManager.getSubscription())
      .then((s) => setTuZapnute(!!s))
      .catch(() => setTuZapnute(false));
  }, [stav]);

  const zapni = async () => {
    setBusy(true);
    try {
      const povolenie = await Notification.requestPermission();
      if (povolenie !== 'granted') {
        throw new Error(povolenie === 'denied'
          ? 'Upozornenia ste v tomto prehliadači zakázali. Povoliť sa dajú v nastaveniach stránky (ikona vedľa adresy).'
          : 'Bez povolenia sa upozornenia poslať nedajú.');
      }
      const reg = await navigator.serviceWorker.ready;
      const odber = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: klucNaBajty(stav.kluc),
      });
      await api.post('/api/upozornenia/prihlas', odber.toJSON());
      setTuZapnute(true);
      toast.success('Upozornenia sú na tomto zariadení zapnuté.');
      nacitaj();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const vypni = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const odber = await reg.pushManager.getSubscription();
      if (odber) {
        await api.post('/api/upozornenia/odhlas', { endpoint: odber.endpoint }).catch(() => {});
        await odber.unsubscribe();
      }
      setTuZapnute(false);
      toast.success('Na tomto zariadení už bzučať nebude.');
      nacitaj();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const skuska = async () => {
    setBusy(true);
    try {
      await api.post('/api/upozornenia/skuska', {});
      toast.success('Odoslané – o chvíľu by malo prísť.');
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const prepni = async (druh, hodnota) => {
    setStav((s) => ({ ...s, volby: { ...s.volby, [druh]: hodnota } }));
    try {
      const d = await api.put('/api/upozornenia/volby', { [druh]: hodnota });
      setStav((s) => ({ ...s, volby: d.volby }));
    } catch (e) { toast.error(e.message); nacitaj(); }
  };

  const zmazZariadenie = async (id) => {
    try {
      await api.del(`/api/upozornenia/zariadenie/${id}`);
      nacitaj();
    } catch (e) { toast.error(e.message); }
  };

  if (!podporovane()) {
    return (
      <p className="text-sm text-slate-500">
        Tento prehliadač upozornenia nevie. Na Androide funguje Chrome, na iPhone treba systém
        najprv pridať na plochu.
      </p>
    );
  }
  if (!stav) return <p className="text-sm text-slate-500">Načítavam…</p>;

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-slate-200 p-3">
        <div className="flex items-center gap-3">
          {tuZapnute ? <Bell size={18} className="text-emerald-600" /> : <BellOff size={18} className="text-slate-400" />}
          <div className="min-w-0 flex-1">
            <div className="font-medium">
              {tuZapnute ? 'Na tomto zariadení zapnuté' : 'Na tomto zariadení vypnuté'}
            </div>
            <div className="text-xs text-slate-500">
              Zapína sa zvlášť na každom telefóne aj počítači – povolenie patrí k prehliadaču.
            </div>
          </div>
          {tuZapnute ? (
            <button className="btn-ghost" disabled={busy} onClick={vypni}>Vypnúť</button>
          ) : (
            <button className="btn-primary" disabled={busy} onClick={zapni}>Zapnúť</button>
          )}
        </div>
        {tuZapnute && (
          <button className="btn-ghost mt-2 text-xs" disabled={busy} onClick={skuska}>
            <Send size={13} /> Poslať skúšobné upozornenie
          </button>
        )}
      </div>

      <div>
        <div className="mb-2 text-sm font-medium">Na čo bzučať</div>
        <div className="space-y-1">
          {Object.entries(stav.druhy || {}).map(([kluc, d]) => (
            <label key={kluc} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!stav.volby?.[kluc]}
                onChange={(e) => prepni(kluc, e.target.checked)}
              />
              {d.nazov}
            </label>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Platí to pre všetky vaše zariadenia naraz – „čo" sa nastavuje raz, „kde" na každom zvlášť.
        </p>
      </div>

      {!!stav.zariadenia?.length && (
        <div>
          <div className="mb-2 text-sm font-medium">Prihlásené zariadenia</div>
          <div className="space-y-1">
            {stav.zariadenia.map((z) => (
              <div key={z.id} className="flex items-center gap-2 rounded border border-slate-200 px-2 py-1 text-xs">
                <Smartphone size={13} className="shrink-0 text-slate-400" />
                <div className="min-w-0 flex-1">
                  <div className="truncate">{popisZariadenia(z.zariadenie)}</div>
                  <div className="text-slate-400">
                    {z.last_ok_at ? `naposledy doručené ${z.last_ok_at}` : 'zatiaľ nič nedostalo'}
                    {z.chyb ? ` · ${z.chyb}× chyba: ${z.posledna_chyba || ''}` : ''}
                  </div>
                </div>
                <button className="text-slate-400 hover:text-rose-600" title="Odobrať"
                  onClick={() => zmazZariadenie(z.id)}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* Z celej hlavičky prehliadača sa dá prečítať len to podstatné – celý reťazec
 * je nečitateľný a pre človeka, ktorý hľadá „ktorý z týchto je môj starý
 * telefón", nepoužiteľný. */
function popisZariadenia(ua = '') {
  const s = String(ua || '');
  const system = /Android/i.test(s) ? 'Android'
    : /iPhone|iPad|iOS/i.test(s) ? 'iPhone alebo iPad'
      : /Windows/i.test(s) ? 'Windows'
        : /Mac OS/i.test(s) ? 'Mac'
          : /Linux/i.test(s) ? 'Linux' : '';
  const prehliadac = /Edg\//i.test(s) ? 'Edge'
    : /OPR\/|Opera/i.test(s) ? 'Opera'
      : /Vivaldi/i.test(s) ? 'Vivaldi'
        : /SamsungBrowser/i.test(s) ? 'Samsung Internet'
          : /Firefox/i.test(s) ? 'Firefox'
            : /Chrome/i.test(s) ? 'Chrome'
              : /Safari/i.test(s) ? 'Safari' : '';
  return [system, prehliadac].filter(Boolean).join(' · ') || 'Neznáme zariadenie';
}
